import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import ApprenantPageShell from './ApprenantPageShell';
import { authService } from '../../services/authService';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import { formatMoney } from '../../utils/currency';

type ResultStatus = 'SUCCESS' | 'FAILED' | 'PENDING' | 'TIMEOUT';

function normalizeStatus(raw?: string | null): ResultStatus {
  const v = (raw ?? '').toUpperCase();
  if (v === 'FAILED') return 'FAILED';
  if (v === 'TIMEOUT') return 'TIMEOUT';
  if (v === 'PENDING') return 'PENDING';
  return 'SUCCESS';
}

const STATUS_CONFIG: Record<
  ResultStatus,
  {
    icon: string;
    tone: string;
    bg: string;
    border: string;
    title: string;
    defaultSubtitle: string;
  }
> = {
  SUCCESS: {
    icon: 'check_circle',
    tone: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    title: 'Paiement réussi',
    defaultSubtitle:
      'Votre paiement a bien été enregistré. Votre inscription sera confirmée dès réception des fonds.',
  },
  FAILED: {
    icon: 'error',
    tone: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
    title: 'Paiement échoué',
    defaultSubtitle:
      'La transaction n’a pas abouti. Vérifiez votre solde ou votre numéro Mobile Money, puis réessayez.',
  },
  TIMEOUT: {
    icon: 'schedule',
    tone: 'text-amber-700',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    title: 'Délai de confirmation dépassé',
    defaultSubtitle:
      'Nous n’avons pas reçu de confirmation à temps. Si vous avez validé sur votre téléphone, consultez votre inscription ; sinon réessayez le paiement.',
  },
  PENDING: {
    icon: 'schedule',
    tone: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    title: 'Paiement en attente',
    defaultSubtitle:
      'La confirmation peut prendre encore quelques minutes. Revenez sur le détail de votre inscription.',
  },
};

type ApprenantPaymentResultPanelProps = {
  onFraisPaid?: (user: unknown) => void;
};

export default function ApprenantPaymentResultPanel({ onFraisPaid }: ApprenantPaymentResultPanelProps) {
  const [params] = useSearchParams();
  const status = normalizeStatus(params.get('status'));
  const amount = Number(params.get('amount') ?? 0);
  const reference = params.get('reference')?.trim() || '—';
  const inscriptionId = params.get('inscriptionId')?.trim();
  const isFraisCompte = params.get('flow') === 'frais-compte';
  const customMessage = params.get('message')?.trim();

  const config = STATUS_CONFIG[status];
  const subtitle =
    customMessage ||
    (isFraisCompte && status === 'SUCCESS'
      ? "Frais d'inscription réglés. Vous pouvez accéder aux formations."
      : config.defaultSubtitle);
  const canRetry = status === 'FAILED' || status === 'TIMEOUT';

  useEffect(() => {
    if (!isFraisCompte || status !== 'SUCCESS' || !onFraisPaid) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const profile = await authService.getCurrentUser();
        if (!cancelled) onFraisPaid(profile);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isFraisCompte, status, onFraisPaid]);

  return (
    <ApprenantPageShell
      title={config.title}
      description={subtitle}
      sideCard={{
        label: 'Montant',
        value: formatMoney(amount, 'CDF'),
        helper: reference !== '—' ? `Réf. ${reference}` : null,
        sideLinks: inscriptionId
          ? [{ label: 'Mon inscription', to: `/app/inscription/${inscriptionId}`, icon: 'assignment' }]
          : [{ label: 'Accueil', to: '/app', icon: 'dashboard' }],
      }}
    >
      <div className="mx-auto max-w-lg space-y-8">
      <div
        className={`rounded-2xl border p-8 text-center shadow-sm ${config.bg} ${config.border}`}
        role="status"
        aria-live="polite"
      >
        <div className={`mx-auto flex h-20 w-20 items-center justify-center rounded-full ${config.bg}`}>
          <LucideSymbolIcon name={config.icon} tone="inherit" size={48} className={config.tone} />
        </div>
        <h1 className={`mt-4 text-2xl font-black ${status === 'SUCCESS' ? 'text-emerald-900' : 'text-slate-900'}`}>
          {config.title}
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-slate-700">{subtitle}</p>
        <p className="mt-5 text-xl font-black text-primary">{formatMoney(amount, 'CDF')}</p>
        {reference !== '—' ? (
          <p className="mt-1 text-xs text-on-surface-variant">Référence : {reference}</p>
        ) : null}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
        {canRetry && isFraisCompte ? (
          <Link
            to="/app/formations"
            className="rounded-xl bg-primary px-6 py-3 text-center text-sm font-bold text-white"
          >
            Réessayer le paiement
          </Link>
        ) : null}
        {canRetry && !isFraisCompte && inscriptionId ? (
          <Link
            to={`/app/inscription/${inscriptionId}/paiement`}
            state={{ montant: amount }}
            className="rounded-xl bg-primary px-6 py-3 text-center text-sm font-bold text-white"
          >
            Réessayer le paiement
          </Link>
        ) : null}
        {inscriptionId ? (
          <Link
            to={`/app/inscription/${inscriptionId}`}
            className={`rounded-xl px-6 py-3 text-center text-sm font-bold ${
              canRetry
                ? 'border border-outline-variant/30 text-slate-800'
                : 'bg-primary text-white'
            }`}
          >
            Voir mon inscription
          </Link>
        ) : null}
        {status === 'SUCCESS' && isFraisCompte ? (
          <Link
            to="/app"
            className="rounded-xl bg-primary px-6 py-3 text-center text-sm font-bold text-white"
          >
            Accéder à l&apos;espace apprenant
          </Link>
        ) : (
          <Link
            to="/app"
            className="rounded-xl border border-outline-variant/30 px-6 py-3 text-center text-sm font-bold text-slate-800"
          >
            Accueil
          </Link>
        )}
      </div>
      </div>
    </ApprenantPageShell>
  );
}
